import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  CurrencyDollar, ShoppingCart, TrendUp, Users, Package,
  CheckCircle, Clock, XCircle, ArrowsClockwise,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 30 * 1000;
const fmt = (n) => (n || 0).toLocaleString('es-AR');
const fmtCurrency = (n) => `$${fmt(n)}`;

const PLATFORM_COLORS = {
  Instagram: '#E1306C',
  TikTok: '#FF0050',
  Facebook: '#1877F2',
  Otro: '#8B5CF6',
};

const StatBig = ({ label, value, sub, icon: Icon, accent = 'bg-primary/10 text-primary', testid }) => (
  <Card className="p-5 border-border" data-testid={testid}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon size={22} weight="duotone" />
      </div>
    </div>
    <div className="text-2xl sm:text-3xl font-bold tabular-nums leading-tight">{value}</div>
    <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    {sub && <div className="text-xs text-muted-foreground/80 mt-2">{sub}</div>}
  </Card>
);

export const OverviewPanel = () => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/admin/stats/extended`, {
        withCredentials: true,
      });
      setData(d);
    } catch (e) {
      /* silent */
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <div className="p-8 text-center">
        <ArrowsClockwise size={28} className="mx-auto animate-spin text-primary" />
        <p className="text-sm text-muted-foreground mt-3">Cargando dashboard...</p>
      </div>
    );
  }

  const r = data.revenue || {};
  const s = data.orders_by_status || {};
  const daily = (data.daily_sales || []).map((d) => ({ ...d, day: d.day.slice(5) }));
  const monthly = (data.monthly_sales || []);
  const platformsForPie = (data.top_platforms || []).map((p) => ({
    name: p.platform,
    value: p.orders,
    color: PLATFORM_COLORS[p.platform] || '#00E676',
  }));

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <button
          onClick={fetchData}
          className="text-xs text-primary flex items-center gap-1 hover:underline"
          data-testid="overview-refresh"
        >
          <ArrowsClockwise size={14} className={refreshing ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {/* Revenue */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatBig testid="stat-revenue-today" label="Hoy" value={fmtCurrency(r.today)} icon={CurrencyDollar} accent="bg-emerald-500/10 text-emerald-500" sub={`${fmt(data.sales_today)} ventas`} />
        <StatBig testid="stat-revenue-week" label="Semana" value={fmtCurrency(r.week)} icon={CurrencyDollar} accent="bg-blue-500/10 text-blue-500" sub={`${fmt(data.sales_week)} ventas`} />
        <StatBig testid="stat-revenue-month" label="Mes" value={fmtCurrency(r.month)} icon={CurrencyDollar} accent="bg-purple-500/10 text-purple-500" sub={`${fmt(data.sales_month)} ventas`} />
        <StatBig testid="stat-revenue-year" label="Año" value={fmtCurrency(r.year)} icon={TrendUp} accent="bg-pink-500/10 text-pink-500" sub={`${fmt(data.sales_year)} ventas`} />
        <StatBig testid="stat-revenue-total" label="Total histórico" value={fmtCurrency(r.total)} icon={CurrencyDollar} sub={`Ganancia est.: ${fmtCurrency(data.estimated_profit_total)} (${data.estimated_margin_pct}%)`} />
      </div>

      {/* Orders by status */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatBig testid="stat-orders-total" label="Pedidos totales" value={fmt(s.total)} icon={ShoppingCart} />
        <StatBig testid="stat-orders-pending" label="Pendientes" value={fmt(s.pending)} icon={Clock} accent="bg-yellow-500/10 text-yellow-500" />
        <StatBig testid="stat-orders-awaiting" label="Esperando pago" value={fmt(s.awaiting_payment)} icon={Clock} accent="bg-orange-500/10 text-orange-500" />
        <StatBig testid="stat-orders-processing" label="En proceso" value={fmt(s.processing)} icon={ArrowsClockwise} accent="bg-blue-500/10 text-blue-500" />
        <StatBig testid="stat-orders-completed" label="Completados" value={fmt(s.completed)} icon={CheckCircle} accent="bg-emerald-500/10 text-emerald-500" />
        <StatBig testid="stat-orders-cancelled" label="Cancelados / falla" value={fmt((s.cancelled || 0) + (s.failed || 0))} icon={XCircle} accent="bg-red-500/10 text-red-500" />
      </div>

      {/* Users */}
      <div className="grid grid-cols-2 gap-3">
        <StatBig testid="stat-users-total" label="Usuarios registrados" value={fmt(data.users?.total)} icon={Users} accent="bg-cyan-500/10 text-cyan-500" />
        <StatBig testid="stat-users-new-month" label="Nuevos este mes" value={fmt(data.users?.new_month)} icon={TrendUp} accent="bg-emerald-500/10 text-emerald-500" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 border-border lg:col-span-2" data-testid="chart-daily-sales">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Ventas últimos 30 días</h3>
            <Badge variant="outline">{daily.length} días</Badge>
          </div>
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(150,150,150,0.15)" />
                <XAxis dataKey="day" stroke="#888" fontSize={11} />
                <YAxis stroke="#888" fontSize={11} />
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="revenue" stroke="#00E676" strokeWidth={2} name="Ingresos" dot={false} />
                <Line type="monotone" dataKey="orders" stroke="#3b82f6" strokeWidth={2} name="Pedidos" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-border" data-testid="chart-platforms">
          <h3 className="font-semibold mb-3">Plataformas (90 días)</h3>
          {platformsForPie.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin datos aún.</p>
          ) : (
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={platformsForPie} dataKey="value" cx="50%" cy="50%" outerRadius={80} label={(p) => `${p.name} (${p.value})`}>
                    {platformsForPie.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Top services */}
      <Card className="p-5 border-border" data-testid="chart-top-services">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Package size={16} /> Servicios más vendidos (90 días)
        </h3>
        {monthly.length > 0 && (
          <div style={{ width: '100%', height: 200 }} className="mb-4">
            <ResponsiveContainer>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(150,150,150,0.15)" />
                <XAxis dataKey="month" stroke="#888" fontSize={11} />
                <YAxis stroke="#888" fontSize={11} />
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="revenue" fill="#00E676" name="Ingresos" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {(data.top_services || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin ventas aún.</p>
        ) : (
          <div className="space-y-2">
            {data.top_services.map((s2, idx) => {
              const max = Math.max(1, ...data.top_services.map((x) => x.orders));
              const pct = (s2.orders / max) * 100;
              return (
                <div key={idx}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-medium">
                      {s2.platform} · {s2.name}
                    </span>
                    <span className="text-muted-foreground tabular-nums">
                      {fmt(s2.orders)} pedidos · {fmtCurrency(s2.revenue)}
                    </span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Recent activity */}
      <Card className="p-5 border-border" data-testid="recent-activity">
        <h3 className="font-semibold mb-3">Actividad reciente</h3>
        {(data.recent_activity || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin actividad todavía.</p>
        ) : (
          <ul className="space-y-1.5 text-sm">
            {data.recent_activity.map((a, i) => (
              <li key={i} className="flex items-baseline gap-2 text-muted-foreground">
                <span className="text-[10px] tabular-nums w-32 shrink-0 text-foreground/60">
                  {a.ts?.slice(0, 16).replace('T', ' ')}
                </span>
                <span className="font-medium text-foreground">{a.admin_email || 'sistema'}</span>
                <span className="text-primary text-xs">{a.action}</span>
                <span className="truncate">{a.target}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};
